import React, { useEffect, useState } from 'react';
import { Pagination, Button, Form, Input, Spin } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import { PlusOutlined, DeleteFilled } from '@ant-design/icons';
import CustomModal from '../../../components/common/CustomModal';
import SortableTableHeader from '../../../components/common/SortableTableHeader';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getQuestion, getTopic, removeQuestion, addQuestion } from '../../../store/slices/adminSlice';
import { useTableState, useModal, useDeleteConfirm, useDataLoading } from '../../../hooks';
import { requiredRule } from '../../../utils';

const { TextArea } = Input;

// TypeScript interfaces
interface TopicData {
  _id: string;
  topicname: string;
}

interface QuestionData {
  _id: string;
  question: string;
  answer: string;
  topic?: TopicData;
}

interface QuestionFormValues {
  question: string;
  answer: string;
  topic?: string;
}

const Question: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<QuestionFormValues>();

  // Redux state
  const questionData = useAppSelector((state) => state.admin.questionData) as QuestionData[];
  const totalCount = useAppSelector((state) => state.admin.questionCount);
  const topicData = useAppSelector((state) => state.admin.topicData) as TopicData[];

  // Use custom hooks for common patterns
  const { currentPage, sortOrder, changePage, sortData } = useTableState({
    fetchAction: getQuestion,
  });

  const { visible, itemId: questionId, showModal, hideModal } = useModal({ form });

  const { showDeleteConfirm } = useDeleteConfirm({
    deleteAction: removeQuestion,
    idParamName: 'questionId',
  });

  const [loading] = useDataLoading(questionData);

  // Local state for topic selection
  const [topicId, setTopicId] = useState<string>('');

  // Load topic data on mount
  useEffect(() => {
    dispatch(getTopic({ page: 0 }));
  }, [dispatch]);

  // Set default topic when data changes
  useEffect(() => {
    if (topicData) {
      const selectedTopic = topicData.find((e) => e.topicname === 'FAQs');
      if (selectedTopic) {
        setTopicId(selectedTopic._id);
      }
    }
  }, [topicData]);

  const handleShowModal = (id: string, question: string, answer: string) => {
    showModal(id);
    if (id !== '') {
      form.setFieldsValue({ question: question, answer: answer });
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const obj = {
        questionId: questionId,
        question: values.question,
        answer: values.answer,
        topicId: topicId,
      };
      hideModal();
      dispatch(addQuestion(obj));
    } catch {
      // Validation failed - form will show errors
    }
  };

  // Render question rows
  const questionRows = questionData.map((x) => (
    <tr key={x._id}>
      <td>{x.question}</td>
      <td>{x.answer}</td>
      <td className="action-icons">
        <Button
          type="link"
          className="btn-sm mr-2"
          onClick={() => handleShowModal(x._id, x.question, x.answer)}
        >
          <i className="fa fa-pencil" style={{ color: '#666' }}></i>
        </Button>
        <Button
          type="link"
          className="btn-sm mr-2"
          onClick={() => showDeleteConfirm(x._id)}
        >
          <DeleteFilled style={{ color: '#666' }} />
        </Button>
      </td>
    </tr>
  ));

  return (
    <>
      <div className="box box-default mb-4">
        <PageHeader
          prefixedClassName="ant-page-header"
          title=" FAQs"
          subTitle=""
          extra={[
            <Button
              key="1"
              type="default"
              onClick={() => handleShowModal('', '', '')}
            >
              <PlusOutlined /> Add FAQs
            </Button>,
          ]}
        />
        <Spin spinning={loading} delay={500}>
          <div className="box-body">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <SortableTableHeader
                      label="FAQs"
                      sortKey="question"
                      currentSortOrder={sortOrder}
                      onSort={sortData}
                    />
                    <SortableTableHeader
                      label="Answer"
                      sortKey="answer"
                      currentSortOrder={sortOrder}
                      onSort={sortData}
                    />
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>{questionRows}</tbody>
              </table>
            </div>
            <Pagination
              style={{ textAlign: 'right' }}
              pageSize={5}
              defaultCurrent={currentPage}
              total={totalCount ? totalCount : 1}
              onChange={changePage}
            />
            <CustomModal
              handleCancel={hideModal}
              handleOk={handleOk}
              visible={visible}
              title="FAQs"
              backBtnText="Back"
              submitBtnText="Submit"
            >
              <Form form={form} className="sport-form l_hgt0" layout="vertical">
                <Form.Item
                  label="FAQs"
                  name="question"
                  rules={[requiredRule('Question')]}
                >
                  <Input placeholder="FAQs" />
                </Form.Item>
                <Form.Item
                  label="Answer"
                  name="answer"
                  rules={[requiredRule('Answer')]}
                >
                  <TextArea placeholder="Answer" />
                </Form.Item>
              </Form>
            </CustomModal>
          </div>
        </Spin>
      </div>
    </>
  );
};

export default Question;
